// Geboortevenster · Generieke tijdlijn met lanes: liniaal per groep, blokken/sporen/markers, geboorte- en versleepbare kandidaatlijn.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { formatBirthClock } from '../birth-window-format';
import {
  HD_PROPERTY_LANE_BLOCK_HEIGHT_PX,
  HD_PROPERTY_LANE_HEIGHT_PX,
  HD_PROPERTY_LANE_MIN_PX_PER_MINUTE,
  HD_PROPERTY_LANE_RULER_HEIGHT_PX,
  HD_PROPERTY_LANE_SNAP_PX,
  HD_PROPERTY_LANE_TRACK_BLOCK_HEIGHT_PX,
  HD_PROPERTY_LANE_TRACK_HEIGHT_PX,
  HdPropertyLaneBlockClick,
  HdPropertyLaneBlockVm,
  HdPropertyLaneGroupVm,
  HdPropertyLaneLabelPart,
  HdPropertyLaneVm,
} from './hd-property-lanes.models';
import { HD_PROPERTY_LANE_BLOCK_PADDING_PX, birthClockMinuteOfDay, fitLaneLabelParts, laneAccentColors } from './hd-property-lanes.text';

interface RenderBlock {
  readonly xPx: number;
  readonly yPx: number;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly textXPx: number;
  readonly textYPx: number;
  readonly parts: readonly HdPropertyLaneLabelPart[];
  readonly block: HdPropertyLaneBlockVm;
  /** Inline kleuren voor accent-blokken (sporen); undefined = staatkleuren via CSS. */
  readonly fill?: string;
  readonly stroke?: string;
  readonly textFill?: string;
}

interface RenderMarker {
  readonly xPx: number;
  readonly glyph: string;
  readonly accent: string;
  readonly title: string;
}

interface RenderLane {
  readonly key: string;
  readonly label: string;
  readonly yPx: number;
  readonly heightPx: number;
  readonly blocks: readonly RenderBlock[];
  readonly markers: readonly RenderMarker[];
  readonly hint?: string;
}

interface RenderGroup {
  readonly key: string;
  readonly label: string;
  readonly hint?: string;
  readonly color?: string;
  readonly muted: boolean;
  readonly collapsible: boolean;
  readonly collapsed: boolean;
  readonly toggleLabel: string;
  readonly heightPx: number;
  readonly lanes: readonly RenderLane[];
}

interface RulerTick {
  readonly xPx: number;
  readonly label: string;
  readonly major: boolean;
}

const BLOCK_PADDING_PX = HD_PROPERTY_LANE_BLOCK_PADDING_PX;
const MINUTE_MS = 60_000;
const TICK_STEPS_MIN = [1, 2, 5, 10, 15, 30, 60, 120, 180, 360];
/** Elke tick krijgt een tijdlabel (feedback Steven); zoveel ruimte heeft "23:30" nodig. */
const MIN_TICK_SPACING_PX = 54;
const DRAG_THRESHOLD_PX = 4;

/**
 * Horizontale tijdas met lanes per eigenschap (spec §3.2 / plan T11). Eén
 * scrollende canvas met een sticky labelkolom; per groep een eigen liniaal
 * (de eerste sticky), de blokken, de geboortelijn en de versleepbare
 * kandidaatlijn zijn SVG. Tijden op de klok van `timeZone`.
 */
@Component({
  selector: 'hd-property-lanes',
  templateUrl: './hd-property-lanes.component.html',
  styleUrl: './hd-property-lanes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdPropertyLanesComponent implements AfterViewInit {
  readonly groups = input.required<readonly HdPropertyLaneGroupVm[]>();
  readonly fromMs = input.required<number>();
  readonly toMs = input.required<number>();
  /** De geboortelijn. */
  readonly anchorMs = input.required<number>();
  readonly anchorLabel = input('');
  /** De kandidaatlijn; `undefined` = niet getoond. */
  readonly markerMs = input<number | undefined>(undefined);
  readonly markerLabel = input('');
  readonly markerDraggable = input(false);
  readonly timeZone = input<string | undefined>(undefined);
  readonly lang = input('nl');
  readonly ariaLabel = input('');

  readonly blockClicked = output<HdPropertyLaneBlockClick>();
  /** Tijdens het slepen (rAF-gethrottled). */
  readonly markerChanged = output<number>();
  /** Bij loslaten. */
  readonly markerCommitted = output<number>();
  /** Klik op lege ruimte of op de liniaal. */
  readonly emptyClicked = output<number>();
  /** In-/uitklapknop van een groep. */
  readonly groupToggled = output<string>();

  protected readonly rulerHeight = HD_PROPERTY_LANE_RULER_HEIGHT_PX;

  private readonly scroller = viewChild.required<ElementRef<HTMLElement>>('scroller');
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly viewportWidthPx = signal(0);
  private readonly labelWidthPx = 150;
  private dragPointerId: number | undefined;
  private dragStartX = 0;
  private dragging = false;
  private dragTarget: Element | undefined;
  private pendingFrame: number | undefined;
  private pendingMs: number | undefined;
  /** Na een sleep onderdrukken we de click die het blok anders nog zou vuren. */
  private suppressNextClick = false;

  protected readonly spanMs = computed(() => Math.max(1, this.toMs() - this.fromMs()));

  protected readonly trackWidthPx = computed(() => {
    const available = Math.max(0, this.viewportWidthPx() - this.labelWidthPx - 2);
    const minimum = (this.spanMs() / MINUTE_MS) * HD_PROPERTY_LANE_MIN_PX_PER_MINUTE;
    return Math.max(available, minimum, 240);
  });

  protected readonly pxPerMs = computed(() => this.trackWidthPx() / this.spanMs());

  protected readonly anchorXPx = computed(() => this.toPx(this.anchorMs()));
  protected readonly markerXPx = computed(() => {
    const ms = this.markerMs();
    return ms === undefined ? undefined : this.toPx(ms);
  });

  protected readonly renderGroups = computed<RenderGroup[]>(() => {
    const trackWidth = this.trackWidthPx();
    return this.groups().map(group => {
      const collapsed = group.collapsible === true && group.collapsed === true;
      const source = collapsed ? group.collapsedLanes ?? [] : group.lanes;
      let y = 0;
      const lanes = source.map(lane => {
        const rendered = this.renderLane(lane, y, trackWidth);
        y += rendered.heightPx;
        return rendered;
      });
      return {
        key: group.key,
        label: group.label,
        hint: group.hint,
        color: group.color,
        muted: group.muted === true,
        collapsible: group.collapsible === true,
        collapsed,
        toggleLabel: (collapsed ? group.expandLabel : group.collapseLabel) ?? '',
        heightPx: y,
        lanes,
      };
    });
  });

  private renderLane(lane: HdPropertyLaneVm, yPx: number, trackWidth: number): RenderLane {
    if (lane.tracks && lane.tracks.length > 0) {
      const heightPx = lane.tracks.length * HD_PROPERTY_LANE_TRACK_HEIGHT_PX;
      const blocks: RenderBlock[] = [];
      lane.tracks.forEach((track, index) => {
        const trackY = index * HD_PROPERTY_LANE_TRACK_HEIGHT_PX;
        for (const block of track.blocks) {
          const rendered = this.renderBlock({ ...block, accent: block.accent ?? track.accent }, trackY, HD_PROPERTY_LANE_TRACK_HEIGHT_PX, HD_PROPERTY_LANE_TRACK_BLOCK_HEIGHT_PX, trackWidth);
          if (rendered) blocks.push(rendered);
        }
      });
      return { key: lane.key, label: lane.label, yPx, heightPx, blocks, markers: [], hint: lane.hint };
    }
    if (lane.markers && lane.markers.length > 0) {
      const from = this.fromMs();
      const to = this.toMs();
      const pxPerMs = this.pxPerMs();
      const markers = lane.markers
        .filter(marker => marker.ms >= from && marker.ms <= to)
        .map(marker => ({ xPx: (marker.ms - from) * pxPerMs, glyph: marker.glyph, accent: marker.accent, title: marker.title }));
      return { key: lane.key, label: lane.label, yPx, heightPx: HD_PROPERTY_LANE_HEIGHT_PX, blocks: [], markers, hint: lane.hint };
    }
    const blocks = lane.blocks
      .map(block => this.renderBlock(block, 0, HD_PROPERTY_LANE_HEIGHT_PX, HD_PROPERTY_LANE_BLOCK_HEIGHT_PX, trackWidth))
      .filter((block): block is RenderBlock => block != null);
    return { key: lane.key, label: lane.label, yPx, heightPx: HD_PROPERTY_LANE_HEIGHT_PX, blocks, markers: [], hint: lane.hint };
  }

  private renderBlock(block: HdPropertyLaneBlockVm, laneY: number, laneHeight: number, blockHeight: number, trackWidth: number): RenderBlock | undefined {
    const from = this.fromMs();
    const to = this.toMs();
    if (block.toMs <= from || block.fromMs >= to) return undefined;
    const pxPerMs = this.pxPerMs();
    const start = Math.max(block.fromMs, from);
    const end = Math.min(block.toMs, to);
    const xPx = (start - from) * pxPerMs;
    const widthPx = Math.max(2, (end - start) * pxPerMs);
    const visibleWidth = Math.min(widthPx, trackWidth - xPx);
    const colors = block.accent ? laneAccentColors(block.accent, block.state) : {};
    return {
      xPx,
      yPx: laneY + (laneHeight - blockHeight) / 2,
      widthPx,
      heightPx: blockHeight,
      textXPx: xPx + BLOCK_PADDING_PX,
      textYPx: laneY + laneHeight / 2 + (blockHeight < 18 ? 3.5 : 4),
      parts: fitLaneLabelParts(block.parts ?? [{ text: block.label, tone: 'neutral' }], visibleWidth),
      block,
      ...colors,
    };
  }

  protected readonly ticks = computed<RulerTick[]>(() => {
    const pxPerMs = this.pxPerMs();
    const from = this.fromMs();
    const to = this.toMs();
    const zone = this.timeZone();
    const lang = this.lang();
    const stepMin = TICK_STEPS_MIN.find(step => step * MINUTE_MS * pxPerMs >= MIN_TICK_SPACING_PX) ?? TICK_STEPS_MIN[TICK_STEPS_MIN.length - 1];
    const stepMs = stepMin * MINUTE_MS;
    // Uitlijnen op hele veelvouden van de stap op de geboorteklok (niet de browserklok).
    const minuteOfDay = birthClockMinuteOfDay(from, zone);
    const remainder = ((minuteOfDay % stepMin) + stepMin) % stepMin;
    let tickMs = from + (remainder === 0 ? 0 : (stepMin - remainder) * MINUTE_MS);
    const ticks: RulerTick[] = [];
    while (tickMs <= to) {
      const minute = birthClockMinuteOfDay(tickMs, zone);
      ticks.push({ xPx: (tickMs - from) * pxPerMs, label: formatBirthClock(tickMs, zone, lang), major: minute % 60 === 0 });
      tickMs += stepMs;
    }
    return ticks;
  });

  /** Alle blokgrenzen en markers (voor de magneet van de sleeplijn). */
  private readonly snapTargets = computed<number[]>(() => {
    const set = new Set<number>([this.anchorMs()]);
    const addBlocks = (blocks: readonly HdPropertyLaneBlockVm[]) => {
      for (const block of blocks) {
        set.add(block.fromMs);
        set.add(block.toMs);
      }
    };
    for (const group of this.groups()) {
      for (const lane of [...group.lanes, ...(group.collapsedLanes ?? [])]) {
        addBlocks(lane.blocks);
        for (const track of lane.tracks ?? []) addBlocks(track.blocks);
        for (const marker of lane.markers ?? []) set.add(marker.ms);
      }
    }
    return [...set];
  });

  ngAfterViewInit(): void {
    const host = this.scroller().nativeElement;
    const measure = () => {
      const width = host.clientWidth;
      this.zone.run(() => this.viewportWidthPx.set(width));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(host);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  protected toPx(ms: number): number {
    return (ms - this.fromMs()) * this.pxPerMs();
  }

  protected signOf(part: HdPropertyLaneLabelPart): string {
    return part.tone === 'added' || part.tone === 'removed' ? part.text.slice(0, 1) : '';
  }

  protected restOf(part: HdPropertyLaneLabelPart): string {
    return part.tone === 'added' || part.tone === 'removed' ? part.text.slice(1) : part.text;
  }

  protected onGroupToggle(groupKey: string): void {
    this.groupToggled.emit(groupKey);
  }

  protected onBlockClick(groupKey: string, laneKey: string, block: HdPropertyLaneBlockVm, event: MouseEvent): void {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    event.stopPropagation();
    this.blockClicked.emit({ groupKey, laneKey, block, origin: { x: event.clientX, y: event.clientY } });
  }

  protected onBlockKeydown(groupKey: string, laneKey: string, block: HdPropertyLaneBlockVm, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const rect = (event.currentTarget as Element).getBoundingClientRect();
    this.blockClicked.emit({ groupKey, laneKey, block, origin: { x: rect.left + rect.width / 2, y: rect.top + rect.height } });
  }

  protected onTrackPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragging = false;
    this.dragTarget = event.currentTarget as Element;
    this.suppressNextClick = false;
  }

  protected onTrackPointerMove(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId || !this.markerDraggable()) return;
    if (!this.dragging) {
      if (Math.abs(event.clientX - this.dragStartX) < DRAG_THRESHOLD_PX) return;
      this.dragging = true;
      this.suppressNextClick = true;
      this.dragTarget?.setPointerCapture?.(event.pointerId);
    }
    this.queueMarker(this.msFromClientX(event, this.dragTarget));
  }

  protected onTrackPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    const target = this.dragTarget;
    this.dragPointerId = undefined;
    this.dragTarget = undefined;
    if (this.dragging) {
      this.dragging = false;
      this.flushMarker();
      this.markerCommitted.emit(this.snap(this.msFromClientX(event, target)));
      target?.releasePointerCapture?.(event.pointerId);
      return;
    }
    // Klik zonder slepen: op een blok handelt het blok zelf af; anders lege ruimte / liniaal.
    const onBlock = (event.target as Element | null)?.closest?.('.pl-block') != null;
    if (!onBlock) {
      this.emptyClicked.emit(this.snap(this.msFromClientX(event, target)));
    }
  }

  protected onTrackPointerCancel(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    this.dragPointerId = undefined;
    this.dragging = false;
    this.dragTarget = undefined;
  }

  private msFromClientX(event: PointerEvent, target: Element | undefined): number {
    const rect = (target ?? (event.currentTarget as Element)).getBoundingClientRect();
    const x = Math.min(Math.max(0, event.clientX - rect.left), this.trackWidthPx());
    return this.fromMs() + x / this.pxPerMs();
  }

  /** Magneet op blokgrenzen en de geboortelijn; anders afronden op hele seconden. */
  private snap(ms: number): number {
    const pxPerMs = this.pxPerMs();
    let best = Math.round(ms / 1000) * 1000;
    let bestDistance = HD_PROPERTY_LANE_SNAP_PX;
    for (const target of this.snapTargets()) {
      const distance = Math.abs(target - ms) * pxPerMs;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = target;
      }
    }
    return Math.min(Math.max(best, this.fromMs()), this.toMs());
  }

  private queueMarker(ms: number): void {
    this.pendingMs = this.snap(ms);
    if (this.pendingFrame !== undefined) return;
    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = undefined;
      this.flushMarker();
    });
  }

  private flushMarker(): void {
    if (this.pendingFrame !== undefined) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = undefined;
    }
    if (this.pendingMs === undefined) return;
    const ms = this.pendingMs;
    this.pendingMs = undefined;
    this.zone.run(() => this.markerChanged.emit(ms));
  }
}
